import { Avatar, ContextMenu } from "@ameelio/ui";
import { Box, Card, MenuItem, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ConnectionFeature,
  ConnectionRestriction,
  ConnectionStatus,
  ConnectionVoiceCallAvailability,
  Correspondent,
  Connection as GQLConnection,
  User,
} from "../api/graphql";
import Link from "../lib/Link";
import VoiceCallsButton from "../VoiceCall/VoiceCallsButton";
import ConnectionStatusChip from "./ConnectionStatusChip";

type Connection = Pick<GQLConnection, "id" | "status" | "features"> & {
  voiceCallAvailability: Pick<
    ConnectionVoiceCallAvailability,
    "currentSlot" | "currentSlotPrivacyLevels" | "nextSlot"
  >;
  activeRestrictions: Pick<ConnectionRestriction, "id" | "service">[];
  contact: Pick<
    Correspondent,
    | "firstName"
    | "lastName"
    | "fullName"
    | "unavailableUntil"
    | "canReceiveVoiceCalls"
  >;
};

export type Props = {
  showVoiceCall: boolean;
  showMessage: boolean;
  showScheduleEvent: boolean;
  connection: Connection;
  user: Pick<User, "__typename">;
};

export default function ConnectionCard({
  showVoiceCall,
  showMessage,
  showScheduleEvent,
  connection,
  user,
}: Props) {
  const { t } = useTranslation();

  // An inmate can make a voice call if there is
  // a current voiceCallSlot.
  const currentVoiceCallSlot =
    connection.voiceCallAvailability.currentSlot || null;
  const currentVoiceCallSlotPrivacyLevels =
    connection.voiceCallAvailability.currentSlotPrivacyLevels || null;
  const nextVoiceCallSlot = connection.voiceCallAvailability.nextSlot || null;
  const userMayMakeVoiceCall =
    user.__typename === "Inmate" && !!currentVoiceCallSlot;

  const navigate = useNavigate();

  const hasVoiceCallRestriction = Boolean(
    connection.activeRestrictions.find((r) => r.service === "VOICE_CALL")
  );

  const actions = [
    showMessage && (
      <MenuItem
        key="action-message"
        onClick={() => navigate(`/messages/${connection.id}`)}
      >
        {t("Message")}
      </MenuItem>
    ),
    showScheduleEvent && (
      <MenuItem
        key="action-schedule-event"
        onClick={() =>
          navigate("/events/new", {
            state: {
              connectionId: connection.id,
              fromPath: `/contacts`,
              fromName: t("Contacts"),
            },
          })
        }
      >
        {t("Schedule event")}
      </MenuItem>
    ),
  ].filter(Boolean);

  return (
    <Card variant="outlined" sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          maxWidth: "100%",
          overflow: "auto",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "center",
          }}
        >
          <Avatar contact={connection.contact} />
          <Link to={`/contacts/${connection.id}`}>
            {connection.contact.fullName}
          </Link>
          {[ConnectionStatus.Pending, ConnectionStatus.Rejected].includes(
            connection.status
          ) && <ConnectionStatusChip status={connection.status} />}
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          component="nav"
          sx={{
            alignItems: "center",
          }}
        >
          {showVoiceCall && (
            <VoiceCallsButton
              showButtonText
              mayMakeVoiceCall={userMayMakeVoiceCall}
              connectionAllowsVoiceCalls={connection.features.includes(
                ConnectionFeature.VoiceCall
              )}
              hasRestriction={hasVoiceCallRestriction}
              currentVoiceCallSlotPrivacyLevels={
                currentVoiceCallSlotPrivacyLevels
              }
              nextVoiceCallSlot={nextVoiceCallSlot}
              connectionId={connection.id}
              contact={connection.contact}
            />
          )}
          {actions.length > 0 && (
            <ContextMenu id={`connection-${connection.id}`}>
              {actions}
            </ContextMenu>
          )}
        </Stack>
      </Box>
    </Card>
  );
}
